
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface ServiceEntry {
  id: string;
  automat_id: string;
  automat_name: string;
  service_type: 'wartung' | 'reparatur' | 'service' | 'inspektion';
  date: string;
  description: string;
  cost: number;
  technician: string;
  duration_minutes: number;
  notes: string;
  is_billable: boolean;
  files: ServiceFile[];
  created_at: string;
}

interface ServiceFile {
  id: string;
  file_name: string;
  file_url: string;
  file_size: number;
  file_type: string;
  uploaded_at: string;
}

interface ServiceQuota {
  id: string;
  automat_id: string;
  automat_name: string;
  wartung_included: number;
  reparatur_included: number;
  service_included: number;
  inspektion_included: number;
  wartung_cost_after: number;
  reparatur_cost_after: number;
  service_cost_after: number;
  inspektion_cost_after: number;
  contract_start_date: string;
  contract_end_date: string;
  notes: string;
}

interface Automat {
  id: string;
  name: string;
  location: string;
}

const serviceTypes = [
  { value: 'wartung', label: 'Wartung', icon: 'ri-tools-line', color: 'bg-blue-100 text-blue-800' },
  { value: 'reparatur', label: 'Reparatur', icon: 'ri-hammer-line', color: 'bg-red-100 text-red-800' },
  { value: 'service', label: 'Service', icon: 'ri-customer-service-line', color: 'bg-green-100 text-green-800' },
  { value: 'inspektion', label: 'Inspektion', icon: 'ri-search-line', color: 'bg-purple-100 text-purple-800' }
];

export default function Wartung() {
  const { user } = useAuth();
  const [serviceEntries, setServiceEntries] = useState<ServiceEntry[]>([]);
  const [serviceQuotas, setServiceQuotas] = useState<ServiceQuota[]>([]);
  const [automaten, setAutomaten] = useState<Automat[]>([]);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isQuotaModalOpen, setIsQuotaModalOpen] = useState(false);
  const [selectedAutomat, setSelectedAutomat] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const [serviceFormData, setServiceFormData] = useState({
    automat_id: '',
    service_type: 'wartung' as 'wartung' | 'reparatur' | 'service' | 'inspektion',
    date: new Date().toISOString().split('T')[0],
    description: '',
    cost: '',
    technician: '',
    duration_minutes: '60',
    notes: '',
    is_billable: false,
    files: [] as File[]
  });

  const [quotaFormData, setQuotaFormData] = useState({
    automat_id: '',
    wartung_included: '6',
    reparatur_included: '0',
    service_included: '3',
    inspektion_included: '2',
    wartung_cost_after: '150',
    reparatur_cost_after: '200',
    service_cost_after: '100',
    inspektion_cost_after: '80',
    contract_start_date: new Date().toISOString().split('T')[0],
    contract_end_date: '',
    notes: ''
  });

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const handleQuotaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedAutomatObj = automaten.find(a => a.id === quotaFormData.automat_id);
    if (!selectedAutomatObj) {
      alert('Bitte wählen Sie einen Automat aus.');
      return;
    }

    try {
      console.log('💾 Starting quota save process...');
      console.log('🔍 Selected automat:', selectedAutomatObj);
      console.log('📝 Form data:', quotaFormData);

      const quotaData = {
        automat_id: quotaFormData.automat_id,
        automat_name: selectedAutomatObj.name,
        wartung_included: parseInt(quotaFormData.wartung_included) || 0,
        reparatur_included: parseInt(quotaFormData.reparatur_included) || 0,
        service_included: parseInt(quotaFormData.service_included) || 0,
        inspektion_included: parseInt(quotaFormData.inspektion_included) || 0,
        wartung_cost_after: parseFloat(quotaFormData.wartung_cost_after) || 0,
        reparatur_cost_after: parseFloat(quotaFormData.reparatur_cost_after) || 0,
        service_cost_after: parseFloat(quotaFormData.service_cost_after) || 0,
        inspektion_cost_after: parseFloat(quotaFormData.inspektion_cost_after) || 0,
        contract_start_date: quotaFormData.contract_start_date,
        contract_end_date: quotaFormData.contract_end_date,
        notes: quotaFormData.notes,
        entry_type: 'service_quota',
        machine_type: 'service_quota'
      };

      console.log('💾 Final quota data:', quotaData);

      // Check if quota already exists for this automat
      const existingQuota = serviceQuotas.find(q => q.automat_id === quotaFormData.automat_id);
      
      if (existingQuota) {
        console.log('🔄 Updating existing quota:', existingQuota.id);
        const { error } = await supabase
          .from('daten')
          .update({ 
            data: quotaData
          })
          .eq('id', existingQuota.id)
          .eq('user_id', user?.id);

        if (error) {
          console.error('❌ Error updating quota:', error);
          alert('Fehler beim Aktualisieren der Vorgaben: ' + error.message);
          return;
        }

        console.log('✅ Quota updated successfully');
        setServiceQuotas(prev => prev.map(q => 
          q.id === existingQuota.id ? { id: existingQuota.id, ...quotaData } : q
        ));
        alert('✅ Vorgaben erfolgreich aktualisiert!');
      } else {
        console.log('➕ Creating new quota with data_type: automat');
        const { data, error } = await supabase
          .from('daten')
          .insert({
            user_id: user?.id,
            data_type: 'automat',
            data: quotaData
          })
          .select()
          .single();

        if (error) {
          console.error('❌ Error creating quota:', error);
          console.error('❌ Error details:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          alert('Fehler beim Erstellen der Vorgaben: ' + error.message);
          return;
        }

        console.log('✅ Quota created successfully:', data);
        setServiceQuotas(prev => [{
          id: data.id,
          ...quotaData
        }, ...prev]);
        alert('✅ Vorgaben erfolgreich gespeichert!');
      }

      setIsQuotaModalOpen(false);
      resetQuotaForm();
    } catch (error) {
      console.error('❌ Unexpected error saving quota:', error);
      alert('Unerwarteter Fehler beim Speichern der Vorgaben: ' + (error as Error).message);
    }
  };

  const handleQuotaInputChange = (field: string, value: string) => {
    setQuotaFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetServiceForm = () => {
    setServiceFormData({
      automat_id: selectedAutomat,
      service_type: 'wartung',
      date: new Date().toISOString().split('T')[0],
      description: '',
      cost: '',
      technician: '',
      duration_minutes: '60',
      notes: '',
      is_billable: false,
      files: []
    });
  };

  const resetQuotaForm = () => {
    setQuotaFormData({
      automat_id: selectedAutomat,
      wartung_included: '6',
      reparatur_included: '0',
      service_included: '3',
      inspektion_included: '2',
      wartung_cost_after: '150',
      reparatur_cost_after: '200',
      service_cost_after: '100',
      inspektion_cost_after: '80',
      contract_start_date: new Date().toISOString().split('T')[0],
      contract_end_date: '',
      notes: ''
    });
  };

  // Upload files to Supabase Storage
  const uploadFiles = async (files: File[]): Promise<ServiceFile[]> => {
    const uploadedFiles: ServiceFile[] = [];
    
    for (const file of files) {
      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${user?.id}/${fileName}`;

        const { data, error } = await supabase.storage
          .from('wartung')
          .upload(filePath, file);

        if (error) {
          console.error('Error uploading file:', error);
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('wartung')
          .getPublicUrl(filePath);

        uploadedFiles.push({
          id: data.path,
          file_name: file.name,
          file_url: publicUrl,
          file_size: file.size,
          file_type: file.type,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    
    return uploadedFiles;
  };

  // Delete file from Supabase Storage
  const deleteFile = async (filePath: string) => {
    try {
      const { error } = await supabase.storage
        .from('wartung')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting file:', error);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setServiceFormData(prev => ({ ...prev, files }));
  };

  // Remove file from selection
  const removeFileFromSelection = (index: number) => {
    setServiceFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const loadData = async () => {
    try {
      console.log('🔄 Loading wartung data...');
      
      // Load automaten
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat');

      if (automatenError) {
        console.error('❌ Error loading automaten:', automatenError);
        throw automatenError;
      }

      console.log('📊 Raw automaten data:', automatenData?.length || 0, 'items');

      const automatenList = automatenData
        ?.filter(item => {
          console.log('🔍 Checking item:', item?.data?.machine_type);
          return item?.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          name: item.data.name,
          location: item.data.location
        })) || [];

      console.log('✅ Filtered automaten:', automatenList.length, 'valid machines');
      setAutomaten(automatenList);

      // Load service entries
      const { data: serviceData, error: serviceError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat')
        .order('created_at', { ascending: false });

      if (serviceError) {
        console.error('❌ Error loading service data:', serviceError);
        throw serviceError;
      }

      console.log('📊 Raw service data:', serviceData?.length || 0, 'items');

      const serviceList = serviceData
        ?.filter(item => item?.data && item.data.entry_type === 'service_entry')
        .map(item => ({
          id: item.id,
          ...item.data
        })) || [];

      console.log('✅ Filtered service entries:', serviceList.length);
      setServiceEntries(serviceList);

      // Load service quotas
      const quotaList = serviceData
        ?.filter(item => item?.data && (item.data.entry_type === 'service_quota' || item.data.machine_type === 'service_quota'))
        .map(item => ({
          id: item.id,
          ...item.data
        })) || [];

      console.log('✅ Filtered service quotas:', quotaList.length);
      setServiceQuotas(quotaList);

    } catch (error) {
      console.error('❌ Error loading wartung data:', error);
      setAutomaten([]);
      setServiceEntries([]);
      setServiceQuotas([]);
    } finally {
      console.log('✅ Wartung data loading completed');
      setLoading(false);
    }
  };

  const handleServiceInputChange = (field: string, value: string | boolean) => {
    setServiceFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedAutomatObj = automaten.find(a => a.id === serviceFormData.automat_id);
    if (!selectedAutomatObj) {
      alert('Bitte wählen Sie einen Automaten aus.');
      return;
    }

    if (!serviceFormData.description.trim()) {
      alert('Bitte geben Sie eine Beschreibung ein.');
      return;
    }

    try {
      console.log('💾 Starting service entry save...');
      
      // Upload files first
      const uploadedFiles = await uploadFiles(serviceFormData.files);

      const serviceData = {
        automat_id: serviceFormData.automat_id,
        automat_name: selectedAutomatObj.name,
        service_type: serviceFormData.service_type,
        date: serviceFormData.date,
        description: serviceFormData.description,
        cost: parseFloat(serviceFormData.cost) || 0,
        technician: serviceFormData.technician,
        duration_minutes: parseInt(serviceFormData.duration_minutes) || 60,
        notes: serviceFormData.notes,
        is_billable: serviceFormData.is_billable,
        files: uploadedFiles,
        created_at: new Date().toISOString(),
        entry_type: 'service_entry',
        machine_type: 'service_entry'
      };

      console.log('💾 Service data to save:', serviceData);

      const { data, error } = await supabase
        .from('daten')
        .insert({
          user_id: user?.id,
          data_type: 'automat',
          data: serviceData
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error saving service entry:', error);
        throw error;
      }

      console.log('✅ Service entry saved successfully:', data);

      setServiceEntries(prev => [{
        id: data.id,
        ...serviceData
      }, ...prev]);

      setIsServiceModalOpen(false);
      resetServiceForm();
      alert('✅ Service-Eintrag erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('❌ Error adding service entry:', error);
      alert('❌ Fehler beim Speichern des Service-Eintrags: ' + (error as Error).message);
    }
  };

  const handleDeleteService = async (id: string) => {
    if (!confirm('Service-Eintrag wirklich löschen?')) return;

    try {
      const serviceEntry = serviceEntries.find(entry => entry.id === id);
      
      if (serviceEntry?.files) {
        for (const file of serviceEntry.files) {
          const filePath = file.id;
          await deleteFile(filePath);
        }
      }

      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setServiceEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Error deleting service entry:', error);
    }
  };

  // Download file
  const downloadFile = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getServiceStats = (automatId: string) => {
    const entries = serviceEntries.filter(entry => entry.automat_id === automatId);
    const quota = serviceQuotas.find(q => q.automat_id === automatId);
    
    const stats = {
      wartung: entries.filter(e => e.service_type === 'wartung').length,
      reparatur: entries.filter(e => e.service_type === 'reparatur').length,
      service: entries.filter(e => e.service_type === 'service').length,
      inspektion: entries.filter(e => e.service_type === 'inspektion').length
    };

    const warnings = [];
    if (quota) {
      if (stats.wartung >= quota.wartung_included) {
        warnings.push({ type: 'wartung', message: `${stats.wartung}/${quota.wartung_included} - weitere CHF ${quota.wartung_cost_after}` });
      }
      if (stats.reparatur >= quota.reparatur_included) {
        warnings.push({ type: 'reparatur', message: `${stats.reparatur}/${quota.reparatur_included} - weitere CHF ${quota.reparatur_cost_after}` });
      }
      if (stats.service >= quota.service_included) {
        warnings.push({ type: 'service', message: `${stats.service}/${quota.service_included} - weitere CHF ${quota.service_cost_after}` });
      }
      if (stats.inspektion >= quota.inspektion_included) {
        warnings.push({ type: 'inspektion', message: `${stats.inspektion}/${quota.inspektion_included} - weitere CHF ${quota.inspektion_cost_after}` });
      }
    }

    return { stats, quota, warnings };
  };

  const getServiceTypeInfo = (type: string) => {
    return serviceTypes.find(t => t.value === type) || serviceTypes[0];
  };

  const openServiceModal = (automatId: string) => {
    setSelectedAutomat(automatId);
    setServiceFormData(prev => ({ ...prev, automat_id: automatId }));
    setIsServiceModalOpen(true);
  };

  const openQuotaModal = (automatId: string) => {
    setSelectedAutomat(automatId);
    const existingQuota = serviceQuotas.find(q => q.automat_id === automatId);
    
    if (existingQuota) {
      setQuotaFormData({
        automat_id: automatId,
        wartung_included: existingQuota.wartung_included.toString(),
        reparatur_included: existingQuota.reparatur_included.toString(),
        service_included: existingQuota.service_included.toString(),
        inspektion_included: existingQuota.inspektion_included.toString(),
        wartung_cost_after: existingQuota.wartung_cost_after.toString(),
        reparatur_cost_after: existingQuota.reparatur_cost_after.toString(),
        service_cost_after: existingQuota.service_cost_after.toString(),
        inspektion_cost_after: existingQuota.inspektion_cost_after.toString(),
        contract_start_date: existingQuota.contract_start_date,
        contract_end_date: existingQuota.contract_end_date || '',
        notes: existingQuota.notes || ''
      });
    } else {
      setQuotaFormData(prev => ({ ...prev, automat_id: automatId }));
    }
    
    setIsQuotaModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Wartungsübersicht...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Wartungs- & Service-Übersicht
                  <span className="ml-3 text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  Übersichtliche Wartungsverwaltung pro Automat ({serviceEntries.length} Einträge)
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {automaten.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-robot-line text-6xl w-16 h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Keine Automaten vorhanden
                </h3>
                <p className="text-gray-500">
                  Fügen Sie zuerst Automaten hinzu, um Wartungen zu verwalten.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {automaten.map(automat => {
                  const { stats, quota, warnings } = getServiceStats(automat.id);
                  const automatEntries = serviceEntries
                    .filter(entry => entry.automat_id === automat.id)
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                  
                  return (
                    <div key={automat.id} className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                      {/* Automat Header */}
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="p-3 bg-blue-200 rounded-lg">
                              <i className="ri-robot-line text-blue-600 text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">{automat.name}</h3>
                              <p className="text-sm text-gray-600">{automat.location}</p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openQuotaModal(automat.id)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors"
                              title="Vorgaben bearbeiten"
                            >
                              <i className="ri-settings-line text-lg"></i>
                            </button>
                            <button
                              onClick={() => openServiceModal(automat.id)}
                              className="p-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg cursor-pointer transition-colors"
                              title="Service hinzufügen"
                            >
                              <i className="ri-add-line text-lg"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Service Statistics */}
                      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-4 gap-4">
                          {serviceTypes.map(type => {
                            const count = stats[type.value as keyof typeof stats];
                            const included = quota ? quota[`${type.value}_included` as keyof ServiceQuota] as number : 0;
                            const isOverLimit = quota && count >= included;
                            
                            return (
                              <div key={type.value} className="text-center">
                                <div className={`p-3 rounded-lg ${isOverLimit ? 'bg-red-100 border border-red-200' : 'bg-white border border-gray-200'}`}>
                                  <i className={`${type.icon} text-lg ${isOverLimit ? 'text-red-600' : 'text-gray-600'} mb-2`}></i>
                                  <div className={`text-lg font-bold ${isOverLimit ? 'text-red-600' : 'text-gray-900'}`}>
                                    {count}
                                  </div>
                                  <div className="text-xs text-gray-500">{type.label}</div>
                                  {quota && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      {included} inkl.
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Warnings */}
                        {warnings.length > 0 && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center mb-2">
                              <i className="ri-alarm-warning-line text-red-600 mr-2"></i>
                              <span className="text-sm font-medium text-red-800">Kostenpflichtige Services</span>
                            </div>
                            <div className="space-y-1">
                              {warnings.map((warning, index) => (
                                <p key={index} className="text-xs text-red-700">
                                  <strong>{serviceTypes.find(t => t.value === warning.type)?.label}:</strong> {warning.message}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quota Info */}
                        {quota && (
                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-blue-800">
                                <strong>Vertrag:</strong> {quota.wartung_included} Wartungen, {quota.service_included} Services inkl.
                              </div>
                              <button
                                onClick={() => openQuotaModal(automat.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                              >
                                Bearbeiten
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Service History */}
                      <div className="px-6 py-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <i className="ri-history-line mr-2"></i>
                          Verlauf ({automatEntries.length})
                        </h4>
                        
                        {automatEntries.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <i className="ri-file-list-line text-2xl mb-2"></i>
                            <p className="text-sm">Noch keine Services erfasst</p>
                          </div>
                        ) : (
                          <div className="space-y-3 max-h-80 overflow-y-auto">
                            {automatEntries.slice(0, 10).map(entry => {
                              const typeInfo = getServiceTypeInfo(entry.service_type);
                              
                              return (
                                <div key={entry.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                  <div className={`p-2 rounded-lg ${typeInfo.color.replace('text-', 'bg-').replace('800', '100')}`}>
                                    <i className={`${typeInfo.icon} text-sm ${typeInfo.color.replace('100', '600')}`}></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeInfo.color}`}>
                                        {typeInfo.label}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(entry.date).toLocaleDateString('de-DE')}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-900 font-medium mb-1">{entry.description}</p>
                                    <div className="flex items-center justify-between text-xs text-gray-600">
                                      <span>{entry.technician || 'Kein Techniker'}</span>
                                      <div className="flex items-center space-x-3">
                                        <span>{entry.duration_minutes} Min.</span>
                                        <span className={entry.cost > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                                          CHF {entry.cost.toFixed(2)}
                                        </span>
                                        <button
                                          onClick={() => handleDeleteService(entry.id)}
                                          className="text-gray-400 hover:text-red-600 cursor-pointer"
                                          title="Löschen"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      </div>
                                    </div>
                                    {entry.notes && (
                                      <p className="text-xs text-gray-500 mt-2 italic">{entry.notes}</p>
                                    )}
                                    
                                    {/* Files Display */}
                                    {entry.files && entry.files.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        <p className="text-xs font-medium text-gray-700">Dateien:</p>
                                        <div className="flex flex-wrap gap-2">
                                          {entry.files.map((file, fileIndex) => (
                                            <button
                                              key={fileIndex}
                                              onClick={() => downloadFile(file.file_url, file.file_name)}
                                              className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 transition-colors cursor-pointer"
                                              title={`${file.file_name} (${(file.file_size / 1024).toFixed(1)} KB)`}
                                            >
                                              <i className="ri-file-line mr-1 text-xs"></i>
                                              <span className="text-xs truncate max-w-20">{file.file_name}</span>
                                              <i className="ri-download-line ml-1 text-xs"></i>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            
                            {automatEntries.length > 10 && (
                              <div className="text-center py-2">
                                <span className="text-xs text-gray-500">
                                  +{automatEntries.length - 10} weitere Einträge
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für neuen Service-Eintrag - KORRIGIERT: Funktionsfähige Event-Handler */}
      <Modal
        isOpen={isServiceModalOpen}
        onClose={() => {
          setIsServiceModalOpen(false);
          resetServiceForm();
        }}
        title="Neuen Service-Eintrag hinzufügen"
        maxWidth="lg"
      >
        <form onSubmit={handleServiceSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Service-Typ"
              options={serviceTypes.map(type => ({
                value: type.value,
                label: type.label
              }))}
              value={serviceFormData.service_type}
              onChange={(value) => handleServiceInputChange('service_type', value)}
              required
            />
            
            <Input
              label="Datum"
              type="date"
              value={serviceFormData.date}
              onChange={(e) => handleServiceInputChange('date', e.target.value)}
              required
            />
            
            <Input
              label="Techniker"
              value={serviceFormData.technician}
              onChange={(e) => handleServiceInputChange('technician', e.target.value)}
              placeholder="Name des Technikers"
            />
            
            <Input
              label="Dauer (Minuten)"
              type="number"
              value={serviceFormData.duration_minutes}
              onChange={(e) => handleServiceInputChange('duration_minutes', e.target.value)}
              placeholder="60"
            />
            
            <Input
              label="Kosten (CHF)"
              type="number"
              step="0.01"
              value={serviceFormData.cost}
              onChange={(e) => handleServiceInputChange('cost', e.target.value)}
              placeholder="0.00"
            />
          </div>
          
          <Input
            label="Beschreibung"
            value={serviceFormData.description}
            onChange={(e) => handleServiceInputChange('description', e.target.value)}
            placeholder="Was wurde gemacht?"
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={serviceFormData.notes}
              onChange={(e) => handleServiceInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={3}
              placeholder="Zusätzliche Informationen..."
            />
          </div>

          {/* File Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dateien anhängen
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.txt"
            />
            <p className="mt-1 text-xs text-gray-500">
              PDF, Word, Excel, Bilder oder Text-Dateien (max. 10MB pro Datei)
            </p>
            
            {/* Selected Files Preview */}
            {serviceFormData.files.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-medium text-gray-700">Ausgewählte Dateien:</p>
                <div className="space-y-2">
                  {serviceFormData.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center space-x-2">
                        <i className="ri-file-line text-gray-500"></i>
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFileFromSelection(index)}
                        className="text-red-500 hover:text-red-700 cursor-pointer"
                        title="Datei entfernen"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_billiable"
              checked={serviceFormData.is_billable}
              onChange={(e) => handleServiceInputChange('is_billable', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_billiable" className="ml-2 block text-sm text-gray-900">
              Kostenpflichtig (wird dem Kunden verrechnet)
            </label>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button 
              type="submit" 
              className="flex-1"
              disabled={!serviceFormData.description.trim()}
            >
              <i className="ri-save-line mr-2"></i>
              Service-Eintrag hinzufügen
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsServiceModalOpen(false);
                resetServiceForm();
              }}
            >
              <i className="ri-close-line mr-2"></i>
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal für Service-Vorgaben */}
      <Modal
        isOpen={isQuotaModalOpen}
        onClose={() => {
          setIsQuotaModalOpen(false);
          resetQuotaForm();
        }}
        title="Service-Vorgaben definieren"
        maxWidth="xl"
      >
        <form onSubmit={handleQuotaSubmit} className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Inkludierte Services</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Input
                label="Wartungen"
                type="number"
                value={quotaFormData.wartung_included}
                onChange={(e) => handleQuotaInputChange('wartung_included', e.target.value)}
                placeholder="6"
              />
              <Input
                label="Reparaturen"
                type="number"
                value={quotaFormData.reparatur_included}
                onChange={(e) => handleQuotaInputChange('reparatur_included', e.target.value)}
                placeholder="0"
              />
              <Input
                label="Services"
                type="number"
                value={quotaFormData.service_included}
                onChange={(e) => handleQuotaInputChange('service_included', e.target.value)}
                placeholder="3"
              />
              <Input
                label="Inspektionen"
                type="number"
                value={quotaFormData.inspektion_included}
                onChange={(e) => handleQuotaInputChange('inspektion_included', e.target.value)}
                placeholder="2"
              />
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Kosten nach Überschreitung (CHF)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Input
                label="Wartung"
                type="number"
                step="0.01"
                value={quotaFormData.wartung_cost_after}
                onChange={(e) => handleQuotaInputChange('wartung_cost_after', e.target.value)}
                placeholder="150.00"
              />
              <Input
                label="Reparatur"
                type="number"
                step="0.01"
                value={quotaFormData.reparatur_cost_after}
                onChange={(e) => handleQuotaInputChange('reparatur_cost_after', e.target.value)}
                placeholder="200.00"
              />
              <Input
                label="Service"
                type="number"
                step="0.01"
                value={quotaFormData.service_cost_after}
                onChange={(e) => handleQuotaInputChange('service_cost_after', e.target.value)}
                placeholder="100.00"
              />
              <Input
                label="Inspektion"
                type="number"
                step="0.01"
                value={quotaFormData.inspektion_cost_after}
                onChange={(e) => handleQuotaInputChange('inspektion_cost_after', e.target.value)}
                placeholder="80.00"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Vertragsbeginn"
              type="date"
              value={quotaFormData.contract_start_date}
              onChange={(e) => handleQuotaInputChange('contract_start_date', e.target.value)}
              required
            />
            <Input
              label="Vertragsende (optional)"
              type="date"
              value={quotaFormData.contract_end_date}
              onChange={(e) => handleQuotaInputChange('contract_end_date', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={quotaFormData.notes}
              onChange={(e) => handleQuotaInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={3}
              placeholder="Zusätzliche Vertragsinformationen..."
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Vorgaben speichern
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsQuotaModalOpen(false);
                resetQuotaForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
